import React, { useState } from 'react';
import { StripeTipCheckoutSelection, TipAmount } from '../../types';
import { useAppointmentDetails, useStepNavigation } from '../organisms/ReviewWrapper';
import reviewService from '../../services/reviewService';
import apruvRateService from '../../services/apruvRateService';
import { createStripeTipCheckoutSession } from '../../services/stripeTipCheckoutService';
import StripeTipCheckout from '../organisms/StripeTipCheckout';
import './Tipping.css';

interface TippingProps {
  isOldReview?: boolean;
  onStepChange?: (step: string) => void;
}

const Tipping: React.FC<TippingProps> = ({ isOldReview = false, onStepChange }) => {
  const appointmentDetails = useAppointmentDetails(); // Get from context
  const { goToThankYou, goToAdd } = useStepNavigation();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const amounts: TipAmount[] = [
    { name: '$5', value: 5 },
    { name: '$10', value: 10 },
    { name: '$15', value: 15 },
  ];

  const rateConfig = apruvRateService.getRateConfig();
  
  // Process dynamic fields in tipping text
  const processDynamicFields = (text: string): string => {
    if (!text || !appointmentDetails) return text || '';

    let processedText = text;

    // Replace technician name placeholders
    const technicianName = appointmentDetails.appointment_details?.TechnicianFirstName || 
                          'Technician';
    processedText = processedText.replace(/\{\{TechnicianName\}\}/g, technicianName);
    processedText = processedText.replace(/\{\{TechnicianFirstName\}\}/g, technicianName);

    // Replace company name placeholders
    const companyName = appointmentDetails.appointment_details?.CompanyName || 
                       'Company';
    processedText = processedText.replace(/\{\{CompanyName\}\}/g, companyName);

    // Replace service date placeholders (using current date as fallback since no service date field exists)
    const serviceDate = new Date().toLocaleDateString();
    processedText = processedText.replace(/\{\{ServiceDate\}\}/g, serviceDate);

    // Replace customer name placeholders
    const customerName = appointmentDetails.appointment_details?.CustomerFirstName || 
                        'Customer';
    processedText = processedText.replace(/\{\{CustomerName\}\}/g, customerName);
    processedText = processedText.replace(/\{\{CustomerFirstName\}\}/g, customerName);

    return processedText;
  };

  // Use dynamic content from rate config, falling back to defaults
  const tippingHeader = processDynamicFields(rateConfig?.TippingHeader || 'Thanks for your feedback!');
  const tippingSubheader = processDynamicFields(rateConfig?.TippingSubHeader || 'Would you like to leave a tip for your technician?');

  const navigateToThankYou = () => {
    if (isOldReview && onStepChange) {
      onStepChange('thank-you');
    } else {
      goToThankYou();
    }
  };

  const goBack = () => {
    if (isOldReview && onStepChange) {
      onStepChange('add');
    } else {
      goToAdd();
    }
  };

  const startStripeCheckout = async ({
    amount,
    coverProcessingFee,
    paymentOption,
    processingFee,
    technicianTip,
    total,
  }: StripeTipCheckoutSelection) => {
    if (!appointmentDetails) return;

    reviewService.setTipAmount(technicianTip);
    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const { appointment_details } = appointmentDetails;
      const response = await createStripeTipCheckoutSession({
        amount,
        appointmentId: appointment_details.AppointmentId.toString(),
        companyName: appointment_details.CompanyName,
        coverProcessingFee,
        customerName: appointment_details.CustomerFirstName,
        paymentOption,
        processingFee,
        returnUrl: window.location.href,
        technicianTip,
        total,
      });
      const checkoutUrl = response.checkoutUrl || response.url;

      if (!checkoutUrl) {
        throw new Error('Stripe checkout session did not return a redirect URL.');
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      setCheckoutError(
        error instanceof Error
          ? error.message
          : 'Stripe checkout is not available. Please try again.'
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="tipping-container">
      <StripeTipCheckout
        amounts={amounts}
        error={checkoutError}
        header={tippingHeader}
        loading={checkoutLoading}
        onBack={goBack}
        onCheckout={startStripeCheckout}
        onNoThanks={navigateToThankYou}
        subheader={tippingSubheader}
      />
    </div>
  );
};

export default Tipping; 
